import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ModelSelector from 'model-hub/scientific-models/input-forms/structure-prediction/ModelSelector';
import NoModelsInstalledEmptyState from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {ScientificModelPreferences} from 'model-hub/scientific-models/ScientificModelConstants';
import {
  resolveInitialStructurePredictionModel,
  type ScientificModelNode,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import {REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY} from './AlphaFoldTabUtils';
import {requestRun} from './AlphaFoldTabUtils';

function RedoPredictionModal({
  availableModels,
  onRequestClose,
  ...props
}: {
  availableModels: ReadonlyArray<ScientificModelNode>;
  aaSequenceId: string;
  folderId: string;
  previouslyRanModel?: ScientificModelNode;
  onRequestClose: () => void;
}): JSX.Element {
  if (availableModels.length === 0) {
    return (
      <Modal title="Redo structure prediction" onRequestClose={onRequestClose} medium={true}>
        <NoModelsInstalledEmptyState />
      </Modal>
    );
  }
  return (
    <LoadedRedoPredictionModal availableModels={availableModels} onRequestClose={onRequestClose} {...props} />
  );
}

function LoadedRedoPredictionModal({
  availableModels,
  aaSequenceId,
  folderId,
  previouslyRanModel,
  onRequestClose,
}: {
  availableModels: ReadonlyArray<ScientificModelNode>;
  aaSequenceId: string;
  folderId: string;
  previouslyRanModel?: ScientificModelNode;
  onRequestClose: () => void;
}): JSX.Element {
  const modalProps = {title: 'Redo structure prediction', onRequestClose, medium: true};
  const queryResult = useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(
    REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY
  );

  const [lastSelectedModel, setLastSelectedModel] = usePreference<string | undefined>(
    ScientificModelPreferences.LAST_SELECTED_STRUCTURE_PREDICTION_MODEL
  );
  const [selectedModel, setSelectedModel] = useState<ScientificModelNode>(
    resolveInitialStructurePredictionModel(
      availableModels,
      previouslyRanModel?.externalReference,
      lastSelectedModel
    )
  );
  const [msaEnabled, setMSAEnabled] = useState<boolean>(() => supportsMSA(selectedModel));

  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading user details. Please refresh the page.');
    onRequestClose();
    return <></>;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return (
      <Modal {...modalProps}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  } else {
    const quota = queryResult.data.modelHubExecutionLimit;
    const remaining = queryResult.data.remainingAlphafoldExecutions;
    const jobQuotaText = `You have ${remaining} of ${quota} requests remaining this month.`;

    const showModelSelector = BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS;

    return (
      <Modal
        {...modalProps}
        buttons={[
          {
            text: 'Predict 3D structure',
            disabled: queryResult.data.remainingAlphafoldExecutions <= 0,
            submit: true,
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              // TODO: For Chai model, the redo functionality should default to all of the components
              // in the prediction that was out of date, not just the primary AA sequence
              requestRun({
                aaSequenceIds: [aaSequenceId],
                moleculeIds: [],
                proteinIds: [],
                primaryComponentId: aaSequenceId,
                folderId,
                remainingExecutionsAndTenantNameData: queryResult.data,
                selectedModel,
                msaEnabled,
              });
              trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_CONFIRM_REDO_RUN);
              onRequestClose();
            },
          },
        ]}
      >
        <>
          {showModelSelector && (
            <div className={css(styles.modelSelectorContainer)}>
              <ModelSelector
                selectedModel={selectedModel}
                availableModels={availableModels}
                msaEnabled={msaEnabled}
                onMSAEnabledChange={setMSAEnabled}
                onModelChange={(model) => {
                  setSelectedModel(model);
                  setMSAEnabled(supportsMSA(model));
                  setLastSelectedModel(model.externalReference);
                }}
              />
            </div>
          )}
          <p className={css(styles.text)}>
            {jobQuotaText} Redoing structure prediction will use up one of those requests.
          </p>
          <p className={css(styles.text)}>
            You will still be able to view the current structure while the new one is being generated.
          </p>
          <p className={css(styles.text)}>Would you like to proceed?</p>
        </>
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  text: FONT_PROXIMA_13,
  modelSelectorContainer: {
    marginBottom: '16px',
  },
});

export default RedoPredictionModal;
